/* eslint-disable complexity */
import { useAuth } from "react-oidc-context";

/**
 * Hook to extract and map user roles from Azure AD role/group claims.
 * Supports environments that return configured role IDs and environments that
 * return plain role names such as "admin" or "clinician".
 */
export const useUserRoles = () => {
  const auth = useAuth();

  // Get role IDs from environment variables
  const adminRoleId = import.meta.env.VITE_AUTH_ROLE_ADMIN || import.meta.env.AUTH_ROLE_ADMIN;
  const clinicianRoleId = import.meta.env.VITE_AUTH_ROLE_CLINICIAN || import.meta.env.AUTH_ROLE_CLINICIAN;
  // console.log("Admin role ID: ", adminRoleId);
  // console.log("Clinician role ID: ", clinicianRoleId);

  const profile = auth?.user?.profile as {
    roles?: unknown;
    groups?: unknown;
  } | undefined;

  const extractStringClaims = (claims: unknown): string[] => {
    if (!Array.isArray(claims)) return [];

    return claims.filter((claim): claim is string => typeof claim === "string");
  };

  const userRoles = Array.from(new Set([
    ...extractStringClaims(profile?.roles),
    ...extractStringClaims(profile?.groups),
  ]));

  // Map group IDs to role names
  const roleNames: string[] = [];

  if ((adminRoleId && userRoles.includes(adminRoleId)) || userRoles.includes("admin")) {
    roleNames.push("admin");
  }

  if ((clinicianRoleId && userRoles.includes(clinicianRoleId)) || userRoles.includes("clinician")) {
    roleNames.push("clinician");
  }

  if (userRoles.includes("user")) {
    roleNames.push("user");
  }

  // If no specific roles found but user has any roles, assume 'user'
  if (roleNames.length === 0 && userRoles.length > 0) {
    roleNames.push("user");
  }

  // console.log("Role names: ", roleNames);

  return {
    // Individual role checks
    isAdmin: roleNames.includes("admin"),
    isClinician: roleNames.includes("clinician"),
    isUser: roleNames.includes("user"),

    // Combined checks
    isClinicianOrAdmin: roleNames.includes("admin") || roleNames.includes("clinician"),

    // Raw data for flexibility
    userRoles,
    roleNames,

    // Debug info
    adminRoleId,
    clinicianRoleId,
  };
};
